import { List } from 'immutable';
import { COUNT_FIELD } from './constants';
import { Aggregations, Directions } from './order';
import { GLOBAL_NULL } from 'reporting-data/constants/defaultNullValues';
const count = series => series.count();
const sum = series => series.reduce((result, v) => result + v, 0);
const average = series => sum(series) / count(series);
const min = series => series.min();
const max = series => series.max();
const distinct = series => series.toSet().count();
const attribute = series => series.first();
const AGGREGATIONS = {
  count,
  sum,
  average,
  min,
  max,
  distinct,
  attribute
};
const getValue = direction => entry => {
  const [value] = entry;
  if (value === undefined || value === null) {
    return direction === Directions.ascending ? Number.MAX_VALUE : Number.MIN_VALUE;
  }
  return value;
};
const getField = (field, aggregation) => entry => {
  const [, rows] = entry;
  const fn = AGGREGATIONS[aggregation];
  const series = rows.map(row => row.get(field));
  if (fn(series) == null) {
    return Number.MIN_VALUE;
  }
  return fn(series);
};
const getDefinedOrderValue = (defined, direction) => entry => {
  const [value] = entry;
  const definedOrderIdx = defined.indexOf(value);
  return definedOrderIdx < 0 && direction === Directions.ascending ? Number.MAX_VALUE : definedOrderIdx;
};
const toComparatorValueMapper = order => {
  const {
    field: field,
    aggregation,
    defined = List(),
    direction
  } = order;
  if (defined.size > 0) {
    return getDefinedOrderValue(defined, direction);
  }
  if (!field) {
    return getValue(direction);
  }
  const aggregationWithFallback = aggregation || (field === COUNT_FIELD ? Aggregations.count : Aggregations.sum);
  return getField(field, aggregationWithFallback);
};

// Based on immutable default comparator: https://github.com/immutable-js/immutable-js/blob/c02d7694f8c72a5589bbad509ffd7fb404486ed2/src/Operations.js#L873-L887
const comparatorWithNullValues = (a, b) => {
  if (a === undefined && b === undefined || a === GLOBAL_NULL && b === GLOBAL_NULL) {
    return 0;
  }

  // Global null is expected to be last in ascending order and first in descending order
  if (a === undefined || a === GLOBAL_NULL) {
    return 1;
  }
  if (b === undefined || b === GLOBAL_NULL) {
    return -1;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, undefined, {
      sensitivity: 'base'
    });
  }
  return a > b ? 1 : a < b ? -1 : 0;
};

/**
 * Selects field values from dataset and returns a sorted list of those values
 * @param field - key of the field to select
 */
export const selectAndSortFieldValues = (dataset, field, order) => {
  const {
    direction,
    defined = List()
  } = order;
  const comparatorValueMapper = toComparatorValueMapper(order);
  const datasetRows = dataset.data; // Dataset.data is typed as List<any>, which isn't enough for detailed type-checking
  const values = datasetRows.groupBy(row => row.get(field)).entrySeq()
  // @ts-expect-error `entrySeq` is not typed properly by `immutable`
  .sortBy(comparatorValueMapper, comparatorWithNullValues).map(([fieldKey]) => fieldKey).toList();
  const definedOrderInDataset = defined.filter(definedOrderValue => values.includes(definedOrderValue));
  return (direction === Directions.ascending ? values : values.reverse()).filter(fieldKey => !definedOrderInDataset.includes(fieldKey)).concat(definedOrderInDataset).toList();
};
export const sortData = (data, field, order) => {
  const {
    direction
  } = order;
  const comparatorValueMapper = toComparatorValueMapper(order);
  return data.groupBy(row => row.get(field)).entrySeq()
  // @ts-expect-error `entrySeq` is not typed properly by `immutable`
  .sortBy(comparatorValueMapper, comparatorWithNullValues).flatMap(([, rows]) => rows).toList().update(rows =>
  // @ts-expect-error `entrySeq` is not typed properly by `immutable`
  direction === Directions.ascending ? rows : rows.reverse());
};

/**
 * Sorts dataset rows by applying order to field
 */
export const sortDataset = (dataset, field, order) => {
  return dataset.update('data', data => sortData(data, field, order));
};